import React, { useMemo } from 'react'
import classNames from 'classnames'
import Styles from './WindDirChart.module.scss'
import {
  CartesianGrid,
  Label,
  Scatter,
  ScatterChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartPt } from 'src/models'
import dayjs, { Dayjs } from 'dayjs'
import { ceil, isNil, max, min, range } from 'lodash'
import { FormatedDate, timeToDDMMHHmm } from './utils'

const WindDirLabelDigit: Record<string, string> = {
  '0': '0°',
  '45': '45°',
  '90': '90°',
  '135': '135°',
  '180': '180°',
  '225': '225°',
  '270': '270°',
  '315': '315°',
  '360': '360°',
}
const WindDirLabelEn: Record<string, string> = {
  '0': 'N',
  '45': 'NE',
  '90': 'E',
  '135': 'SE',
  '180': 'S',
  '225': 'SW',
  '270': 'W',
  '315': 'NW',
  '360': 'N',
}

export const WindDirChart: React.FC<{
  className: string
  syncId: string
  data: ChartPt[]
  xLabel?: string
  yLabel?: string
  yAxisUnit: string
  color?: string
  noMinor?: boolean
  grid?: boolean
  current?: Dayjs
  height?: number
  width?: number
  zoom?: { x: number; scale: number }
}> = ({
  className,
  syncId,
  data: _data,
  xLabel,
  yLabel,
  yAxisUnit,
  color,
  noMinor,
  grid,
  current,
  height,
  width,
  zoom,
}) => {
    const data = _data.map(d => ({ ...d, v: ((isNil(d.v) || isNaN(d.v) || d.v > 360 || d.v < 0) ? null : d.v) }))
    const times = data.map(({ t }) => t)
    const _minX = min(times) ?? 0
    const _maxX = max(times) ?? 0
    const minX = zoom ? zoom.x - (zoom.x - _minX) * zoom.scale : _minX
    const maxX = zoom ? zoom.x + (_maxX - zoom.x) * zoom.scale : _maxX
    const xTicksFull = useMemo(() => {
      const indexes = ceil(dayjs(_maxX).diff(dayjs(_minX), 'hour'))
      const interval = Math.ceil(indexes / 12)
      const start = dayjs(_minX).startOf('hour').unix() * 1000
      return range(indexes).map(i => start + i * interval * 3600_000)
    }, [_minX, _maxX])
    const xTicks = xTicksFull.filter(v => v >= minX && v <= maxX)

    return (
      <div className={classNames(Styles.chart, className, zoom && Styles.scrollable)}>
        {yAxisUnit && <label className={Styles.unit}>{yAxisUnit}</label>}
        <ResponsiveContainer width={width ?? "100%"} height={(height ?? 270) + 22}>
          <ScatterChart
            syncId={syncId}
            margin={{
              top: 40,
              bottom: xLabel ? 12 : -6,
              left: yLabel ? 6 : -24,
              right: 28,
            }}
            data={data}
          >
            {grid && <CartesianGrid className={Styles.grid} strokeDasharray="3 5" />}
            <YAxis
              orientation="left"
              yAxisId='left'
              dataKey={'v'}
              className={Styles.YAxis}
              type="number"
              label={yLabel && <Label className={Styles.yLabel} value={yLabel} />}
              tickMargin={noMinor ? 1 : -7}
              fontSize="1rem"
              domain={[0, 360]}
              interval={0}
              ticks={range(9).map(v => v * 45)}
              tickFormatter={v => WindDirLabelEn[v]}
            />
            <YAxis
              orientation="right"
              dataKey={'v'}
              className={Styles.YAxis}
              type="number"
              label={yLabel && <Label className={Styles.yLabel} value={yLabel} />}
              tickMargin={noMinor ? 1 : -7}
              fontSize="1rem"
              domain={[0, 360]}
              interval={0}
              ticks={range(9).map(v => v * 45)}
              tickFormatter={v => WindDirLabelDigit[v]}
            />
            <XAxis
              className={Styles.XAxis}
              type="number"
              allowDataOverflow={true}
              label={<Label className={Styles.xLabel} value={xLabel} />}
              fontSize="1rem"
              dataKey="t"
              tickFormatter={timeToDDMMHHmm}
              ticks={xTicks}
              domain={[minX, maxX]}
            />
            <Scatter
              dataKey={`v`}
              fill={color}
              shape={({ cx, cy }: { cx: number, cy: number }) => <>
                <svg x={cx - 4} y={cy - 4} width={4} height={4} viewBox="0 0 4 4">
                  <circle cx={2} cy={2} r={2} fill={color} />
                </svg>
              </>}
            />
            <Tooltip
              content={({
                active,
                payload,
                label,
              }: {
                active?: boolean
                payload?: any
                label?: number
              }) => {
                const data = payload?.[0]
                return (
                  active &&
                  data &&
                  !isNil(label) && (
                    <div className={Styles.chartTooltips}>
                      <div className={Styles.time}>{FormatedDate(label)}</div>
                      <div className={Styles.value}>{`${data.value.toFixed(1)}`}</div>
                    </div>
                  )
                )
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
        <div className={Styles.scrollHint}>
          <label>{'scroll to zoom'}</label>
        </div>
      </div>
    )
  } 